import { parse } from 'date-fns';
import React from 'react';

import { EmptyFieldValueRenderer } from '@/components/molecules/EmptyFieldValueRenderer';
import { FieldValue } from '@/components/molecules/FieldValue';
import { TimezoneContext } from '@/hooks/useDateFormatter';
import { LocaleContext } from '@/hooks/useTranslator';
import { DateField, ValueType } from '@/types/fields';
import { Locale } from '@/types/locale';

interface Props {
  readonly value: ValueType;
  readonly field: DateField<unknown>;
}
export const DateFieldValueRenderer: React.FC<Props> = (props: Props): React.ReactElement => {
  const { value } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const timezone = React.useContext<string>(TimezoneContext);

  const formatter = React.useMemo(
    (): Intl.DateTimeFormat =>
      new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: timezone,
      }),
    [locale, timezone],
  );

  const formatted = React.useMemo((): string | null => {
    if (typeof value !== 'string') {
      return null;
    }
    return formatter.format(parse(value, 'yyyy-mm-dd', new Date()));
  }, [formatter, value]);

  if (formatted === null) {
    console.debug('DateFieldValueRenderer: value is not a string');
    return <EmptyFieldValueRenderer />;
  }

  return <FieldValue value={formatted} />;
};
